import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Table,
  InputNumber,
  Input,
  Select,
  Typography,
  Alert,
  Descriptions,
  Row,
  Col,
  Divider,
} from "antd";
import { toast } from "../../utils/toast";
import dayjs from "dayjs";
import { Bill } from "../../types/Billing";
import billingService, {
  BillReturnableLine,
  BillReturnableResponse,
} from "../../services/billingService";

const { Text } = Typography;
const { TextArea } = Input;

interface SaleReturnModalProps {
  bill: Bill | null;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const money = (n?: number) => `₹${Number(n || 0).toFixed(2)}`;

const SaleReturnModal: React.FC<SaleReturnModalProps> = ({
  bill,
  visible,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<BillReturnableResponse | null>(null);
  const [returnQty, setReturnQty] = useState<Record<string, number>>({});
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] = useState<string>("cash");
  const [refundAmount, setRefundAmount] = useState<number | null>(null);
  const [deduction, setDeduction] = useState<number>(0);
  const [refundTouched, setRefundTouched] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !bill?._id) return;
    let cancelled = false;
    setLoading(true);
    setReturnQty({});
    setReason("");
    setRefundMethod("cash");
    setRefundAmount(null);
    setDeduction(0);
    setRefundTouched(false);
    setLoadError(null);
    billingService
      .getBillReturnable(bill._id)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        const initial: Record<string, number> = {};
        for (const line of res.lines) {
          initial[line.medicine] = 0;
        }
        setReturnQty(initial);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setLoadError(
          e?.response?.data?.message || "Failed to load returnable items"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, bill?._id]);

  const suggestedRefund = useMemo(() => {
    if (!data) return 0;
    return Number(
      data.lines
        .reduce((sum, line) => {
          const qty = returnQty[line.medicine] || 0;
          if (qty <= 0) return sum;
          const gross = qty * line.unitPrice;
          const taxable = gross - (gross * (line.discount || 0)) / 100;
          return sum + taxable;
        }, 0)
        .toFixed(2)
    );
  }, [data, returnQty]);

  useEffect(() => {
    if (!refundTouched) {
      setRefundAmount(suggestedRefund > 0 ? suggestedRefund : null);
    }
  }, [suggestedRefund, refundTouched]);

  const netRefund = Math.max(
    0,
    Number(((refundAmount || 0) - (deduction || 0)).toFixed(2))
  );

  const handleSubmit = async () => {
    if (!bill) return;
    const items = Object.entries(returnQty)
      .filter(([, qty]) => qty > 0)
      .map(([medicine, quantity]) => ({ medicine, quantity }));

    if (!items.length) {
      toast.warning("Enter return quantity for at least one item");
      return;
    }
    if (refundAmount == null || refundAmount < 0) {
      toast.warning("Enter refund amount");
      return;
    }

    setSubmitting(true);
    try {
      await billingService.createSaleReturn({
        bill: bill._id,
        items,
        reason: reason.trim() || undefined,
        refundMethod,
        refundAmount,
        deduction: deduction || 0,
      });
      onSuccess();
      onClose();
    } catch {
      // API errors toasted by interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Medicine",
      dataIndex: "name",
      key: "name",
      render: (_: string, row: BillReturnableLine) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Batch {row.batchNumber || "—"}
          </Text>
        </div>
      ),
    },
    {
      title: "Rate",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 80,
      align: "right" as const,
      render: (v: number) => money(v),
    },
    {
      title: "Disc%",
      dataIndex: "discount",
      key: "discount",
      width: 64,
      align: "center" as const,
      render: (v: number) => `${v || 0}%`,
    },
    {
      title: "On bill",
      dataIndex: "onBillQty",
      key: "onBillQty",
      width: 70,
      align: "center" as const,
      render: (_: number, row: BillReturnableLine) =>
        row.onBillQty ?? row.returnableQty,
    },
    {
      title: "Returned",
      dataIndex: "alreadyReturned",
      key: "alreadyReturned",
      width: 72,
      align: "center" as const,
    },
    {
      title: "Line Amt",
      dataIndex: "taxableAmount",
      key: "taxableAmount",
      width: 88,
      align: "right" as const,
      render: (_: number, row: BillReturnableLine) =>
        money(row.taxableAmount ?? row.lineTotal),
    },
    {
      title: "Returnable",
      dataIndex: "returnableQty",
      key: "returnableQty",
      width: 80,
      align: "center" as const,
    },
    {
      title: "Return Qty",
      key: "returnQty",
      width: 100,
      render: (_: unknown, row: BillReturnableLine) => (
        <InputNumber
          controls={false}
          min={0}
          max={row.returnableQty}
          value={returnQty[row.medicine] || 0}
          disabled={row.returnableQty <= 0}
          onChange={(v) =>
            setReturnQty((prev) => ({
              ...prev,
              [row.medicine]: Math.min(
                row.returnableQty,
                Math.max(0, Number(v) || 0)
              ),
            }))
          }
          style={{ width: "100%" }}
        />
      ),
    },
  ];

  const b = data?.bill;
  const customerName =
    b?.patient && typeof b.patient === "object"
      ? (b.patient as any).name
      : b?.customer?.name ||
        (typeof bill?.patient === "object" && bill.patient
          ? bill.patient.name
          : bill?.customer?.name) ||
        "Walk-in";
  const customerPhone =
    b?.patient && typeof b.patient === "object"
      ? (b.patient as any).phone
      : b?.customer?.phone || bill?.customer?.phone || "";

  return (
    <Modal
      title={`Return Medicine — ${bill?.billNumber || ""}`}
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Confirm Return"
      confirmLoading={submitting}
      okButtonProps={{ disabled: !!loadError || loading || !data }}
      width={860}
      destroyOnClose
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Returned qty goes back to stock. The bill is updated (totals & payment). A previous version is saved for history."
      />

      {loadError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={loadError}
        />
      )}

      <Descriptions
        size="small"
        bordered
        column={{ xs: 1, sm: 2, md: 3 }}
        style={{ marginBottom: 12 }}
        title="Bill summary"
      >
        <Descriptions.Item label="Customer">
          {customerName}
          {customerPhone ? ` · ${customerPhone}` : ""}
        </Descriptions.Item>
        <Descriptions.Item label="Bill date">
          {b?.billDate ? dayjs(b.billDate).format("DD MMM YYYY, hh:mm A") : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Paid via">
          {(b?.paymentMethod || "—").toString().replace(/_/g, " ")}
        </Descriptions.Item>
        <Descriptions.Item label="Subtotal (taxable)">
          {money(b?.subtotal)}
        </Descriptions.Item>
        <Descriptions.Item label="Line discount">
          {money(b?.lineDiscountTotal)}
        </Descriptions.Item>
        <Descriptions.Item label="Bill discount">
          {money(b?.billDiscount)}
        </Descriptions.Item>
        <Descriptions.Item label="Tax on bill">
          {money(b?.totalTax)}
        </Descriptions.Item>
        <Descriptions.Item label="Round off">
          {money(b?.roundOff)}
        </Descriptions.Item>
        <Descriptions.Item label="Bill amount">
          <Text strong>{money(b?.grandTotal)}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Bill version">
          v{b?.version || 1}
        </Descriptions.Item>
        <Descriptions.Item label="Amount paid">
          {money(b?.amountPaid)}
        </Descriptions.Item>
        <Descriptions.Item label="Balance">
          {money(b?.balance)}
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          {(b?.paymentStatus || "—").toString().replace(/_/g, " ")}
        </Descriptions.Item>
      </Descriptions>

      <Table
        size="small"
        rowKey="medicine"
        loading={loading}
        pagination={false}
        columns={columns}
        dataSource={data?.lines || []}
        locale={{ emptyText: "No items" }}
        scroll={{ x: 720 }}
        style={{ marginBottom: 8 }}
      />

      <Text type="secondary" style={{ fontSize: 12 }}>
        Suggested for selected qty (after line disc%): {money(suggestedRefund)}
        {" — "}
        use as a guide; enter the actual refund below.
      </Text>

      <Divider style={{ margin: "12px 0" }} />

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={8}>
          <div style={{ marginBottom: 4, fontSize: 12 }}>Mode of refund</div>
          <Select
            value={refundMethod}
            onChange={setRefundMethod}
            style={{ width: "100%" }}
            options={[
              { value: "cash", label: "Cash" },
              { value: "upi", label: "UPI" },
              { value: "card", label: "Card" },
              { value: "net_banking", label: "Net Banking" },
              { value: "adjustment", label: "Adjustment / credit" },
            ]}
          />
        </Col>
        <Col xs={24} sm={8}>
          <div style={{ marginBottom: 4, fontSize: 12 }}>
            Refund amount (₹) *
          </div>
          <InputNumber
            controls={false}
            min={0}
            precision={2}
            value={refundAmount}
            placeholder="Enter amount"
            onChange={(v) => {
              setRefundTouched(true);
              setRefundAmount(v == null ? null : Number(v));
            }}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <div style={{ marginBottom: 4, fontSize: 12 }}>
            Deduction before refund (₹)
          </div>
          <InputNumber
            controls={false}
            min={0}
            precision={2}
            value={deduction}
            placeholder="e.g. opened pack fee"
            onChange={(v) => setDeduction(v || 0)}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      <div
        style={{
          marginTop: 12,
          padding: "10px 12px",
          background: "#fafafa",
          borderRadius: 6,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          Refund {money(refundAmount || 0)} − Deduction {money(deduction)}
        </Text>
        <Text strong style={{ fontSize: 15 }}>
          Net refund: {money(netRefund)}
        </Text>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ marginBottom: 4, fontSize: 12 }}>Reason (optional)</div>
        <TextArea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Customer returned unused strips"
        />
      </div>

      {(data?.priorReturns?.length || 0) > 0 && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Previous returns:{" "}
            {data!.priorReturns
              .map((r) => {
                const amt =
                  r.netRefundAmount ?? r.refundAmount ?? r.grandTotal ?? 0;
                return `${r.returnNumber} (${money(amt)})`;
              })
              .join(" · ")}
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default SaleReturnModal;
